import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SEO = ({ 
  title = "Tomorrow Global - Agence Média 360",
  description = "Tomorrow Global, agence média 360 dédiée à l'excellence et à l'innovation. Nous créons des stratégies novatrices et intégrons les dernières avancées technologiques pour garantir le succès de nos clients.",
  keywords = "agence média, publicité, achat média, production audiovisuelle, marketing digital, influence marketing, communication, stratégie marketing, campagnes publicitaires, ROI, Impact 2.0",
  image = "/logo_tomorrow.png",
  type = "website",
  noindex = false
}) => {
  const location = useLocation();
  const baseUrl = "https://tomorrow-global.com";
  const currentUrl = `${baseUrl}${location.pathname}`;

  useEffect(() => {
    // Update document title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name, content, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow');
    
    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', `${baseUrl}${image}`, true);
    updateMetaTag('og:url', currentUrl, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:site_name', 'Tomorrow Global', true);
    updateMetaTag('og:locale', 'fr_FR', true);
    
    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', `${baseUrl}${image}`);
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', currentUrl);
    
    // Language
    document.documentElement.setAttribute('lang', 'fr');

    // Structured Data (JSON-LD)
    const removeExistingSchema = () => {
      const existing = document.querySelectorAll('script[type="application/ld+json"]');
      existing.forEach(el => el.remove());
    };

    removeExistingSchema();

    // Organization Schema
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Tomorrow Global",
      "alternateName": "Tomorrow",
      "url": baseUrl,
      "logo": `${baseUrl}/logo_tomorrow.png`,
      "description": "Agence média 360 dédiée à l'excellence et à l'innovation. Communication, publicité, marketing digital et production audiovisuelle.",
      "address": [
        {
          "@type": "PostalAddress",
          "streetAddress": "Rue de l'Euro, Immeuble Selim, Les Berges Du Lac II",
          "addressLocality": "Tunis",
          "addressCountry": "TN"
        },
        {
          "@type": "PostalAddress",
          "streetAddress": "1288, Rue Saint-Antoine Ouest",
          "addressLocality": "Montreal",
          "addressRegion": "Quebec",
          "postalCode": "H3C 1C2",
          "addressCountry": "CA"
        }
      ],
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "+216-52-814-814",
          "contactType": "customer service",
          "areaServed": "TN",
          "availableLanguage": ["French", "English", "Arabic"]
        },
        {
          "@type": "ContactPoint",
          "telephone": "+1-438-507-9996",
          "contactType": "customer service",
          "areaServed": "CA",
          "availableLanguage": ["French", "English"]
        }
      ],
      "email": "hello@tomorrow-global.com",
      "foundingDate": "2017",
      "sameAs": [
        "https://www.facebook.com/tomorrowglobal",
        "https://www.twitter.com/tomorrowglobal"
      ]
    };

    // Service Schema
    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "name": "Tomorrow Global",
      "description": "Services complets de communication et publicité : Achat Media, Production Audiovisuelle, Digital Media, Consulting, Design, Événementielle, Animation et Influence Marketing",
      "provider": {
        "@type": "Organization",
        "name": "Tomorrow Global",
        "url": baseUrl
      },
      "serviceType": [
        "Achat Media",
        "Production Audiovisuelle",
        "Digital Media",
        "Consulting & Stratégie",
        "Design & Créativité",
        "Événementielle",
        "Animation 2D & 3D",
        "Influence Marketing"
      ],
      "areaServed": [
        {
          "@type": "Country",
          "name": "Tunisia"
        },
        {
          "@type": "Country",
          "name": "Canada"
        },
        {
          "@type": "Place",
          "name": "International"
        }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Services Tomorrow Global",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Achat Media",
              "description": "Gestion de l'allocation du budget d'achat d'espace publicitaire sur tous supports"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Production Audiovisuelle",
              "description": "Création de contenus audiovisuels de qualité : spots publicitaires, vidéos, documentaires"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Digital Media",
              "description": "Gestion des réseaux sociaux, SEO/SEA, campagnes publicitaires en ligne"
            }
          }
        ]
      }
    };

    // Breadcrumb Schema (for better navigation understanding)
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Accueil",
          "item": baseUrl
        }
      ]
    };

    // Add current page to breadcrumb if not home
    if (location.pathname !== '/') {
      const pathSegments = location.pathname.split('/').filter(Boolean);
      pathSegments.forEach((segment, index) => {
        const name = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
        breadcrumbSchema.itemListElement.push({
          "@type": "ListItem",
          "position": index + 2,
          "name": name,
          "item": `${baseUrl}/${pathSegments.slice(0, index + 1).join('/')}`
        });
      });
    }

    // Add schemas to page
    const addSchema = (schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    };

    addSchema(organizationSchema);
    addSchema(serviceSchema);
    addSchema(breadcrumbSchema);

  }, [title, description, keywords, image, type, noindex, currentUrl, baseUrl, location]);

  return null;
};

export default SEO;
